import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { zoneService } from '@/services/zoneService';
import type { DeneignementZone, StatutZone } from '@/types';
import {
  STATUT_ZONE_LABELS,
  STATUT_ZONE_COLORS,
  TYPE_ENGIN_LABELS,
  TYPE_ENGIN_COLORS,
} from '@/types';
import {
  CheckCircleIcon,
  PlayIcon,
  ClockIcon,
  TruckIcon,
  UserGroupIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/solid';
import {
  CheckCircleIcon as CheckCircleOutline,
  ExclamationTriangleIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import {
  StarIcon as StarIconSolid,
} from '@heroicons/react/24/solid';

/**
 * =============================================================================
 * MAPPING ZONE DEMANDEE -> ZONES TERRAIN PRIORITAIRES
 * =============================================================================
 *
 * SFD v2.2: Relation entre la zone demandee dans l'intervention et les
 * 6 zones terrain predefinies.
 *
 * PRINCIPE:
 * - La demande d'intervention specifie une "zone" (complete/avion/voies/equipements)
 * - Les agents terrain travaillent sur 6 zones physiques predefinies
 * - Ce mapping indique quelles zones physiques correspondent a la demande
 *
 * SECURITE:
 * - Les zones prioritaires sont INDICATIVES uniquement
 * - L'agent garde toute liberte d'intervenir sur n'importe quelle zone
 * - La securite de l'avion et des intervenants prime sur les priorites
 *
 * ZONES TERRAIN (6 zones predefinies):
 *   1. Parking bus (Pony)
 *   2. Cheminement PAX (Pony)
 *   3. Hydrant carburant (Pony)
 *   4. Cheminement BAG (Pony)
 *   5. Poste push 1 (Tracteur)
 *   6. Poste push 2 (Tracteur)
 */
export const ZONE_PRIORITE_MAPPING: Record<string, number[]> = {
  /**
   * Zone "complete": Deneigement integral du parking
   * Toutes les 6 zones sont prioritaires
   */
  complete: [1, 2, 3, 4, 5, 6],

  /**
   * Zone "avion": Zones directement autour de l'aeronef
   * - Zone 3: Hydrant carburant (acces ravitaillement)
   * - Zone 5: Poste push 1 (manoeuvre tracteur)
   * - Zone 6: Poste push 2 (manoeuvre tracteur)
   */
  avion: [3, 5, 6],

  /**
   * Zone "voies": Voies de circulation sur le parking
   * - Zone 2: Cheminement PAX (passage passagers)
   * - Zone 4: Cheminement BAG (passage bagages)
   */
  voies: [2, 4],

  /**
   * Zone "equipements": Equipements et infrastructures
   * - Zone 1: Parking bus (zone bus navette)
   * - Zone 3: Hydrant carburant (borne carburant)
   */
  equipements: [1, 3],
};

/**
 * Props du composant ZoneChecklist
 *
 * @interface ZoneChecklistProps
 */
interface ZoneChecklistProps {
  /** ID de l'intervention (deneignement) */
  deneignementId: number;

  /** Liste des zones a afficher avec leur statut */
  zones: DeneignementZone[];

  /** Pourcentage de progression (0-100) */
  progression: number;

  /** Callback appele apres mise a jour d'une zone */
  onZoneUpdate?: () => void;

  /** Mode lecture seule (intervention terminee) */
  readOnly?: boolean;

  /**
   * Zone demandee dans l'intervention
   * Permet d'afficher les priorites selon le mapping ZONE_PRIORITE_MAPPING
   *
   * Valeurs possibles:
   * - 'complete': Toutes les zones prioritaires
   * - 'avion': Zones autour de l'aeronef (3, 5, 6)
   * - 'voies': Voies de circulation (2, 4)
   * - 'equipements': Equipements (1, 3)
   *
   * @see ZONE_PRIORITE_MAPPING
   */
  zoneDemandee?: string;
}

/**
 * =============================================================================
 * COMPOSANT ZONECHECKLIST
 * =============================================================================
 *
 * Checklist interactive des zones pour l'interface mobile terrain.
 *
 * FONCTIONNALITES:
 * - Affichage des 6 zones avec statut (a_faire/en_cours/termine)
 * - Boutons tactiles grands format (min 48px) pour usage avec gants
 * - Indication visuelle des zones prioritaires selon la demande
 * - Horodatage automatique debut/fin de chaque zone
 * - Commentaire optionnel par zone
 *
 * PRIORITES (SFD v2.2):
 * - Les zones prioritaires sont marquees d'une etoile
 * - L'agent peut neanmoins traiter n'importe quelle zone
 * - La securite prime sur les priorites definies
 *
 * @component
 * @version 2.2
 */
export function ZoneChecklist({
  deneignementId,
  zones,
  progression,
  onZoneUpdate,
  readOnly = false,
  zoneDemandee,
}: ZoneChecklistProps) {
  const queryClient = useQueryClient();
  const [expandedZone, setExpandedZone] = useState<number | null>(null);
  const [commentaire, setCommentaire] = useState('');

  // Determiner les zones prioritaires selon la demande
  const zonesPrioritaires = zoneDemandee
    ? ZONE_PRIORITE_MAPPING[zoneDemandee] || []
    : [];

  // Fonction pour verifier si une zone est prioritaire
  const isZonePrioritaire = (numeroZone: number) => zonesPrioritaires.includes(numeroZone);

  // Mutation pour demarrer une zone
  const demarrerMutation = useMutation({
    mutationFn: (zoneId: number) => zoneService.demarrerZone(deneignementId, zoneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
      onZoneUpdate?.();
    },
  });

  // Mutation pour terminer une zone
  const terminerMutation = useMutation({
    mutationFn: ({ zoneId, commentaire }: { zoneId: number; commentaire?: string }) =>
      zoneService.terminerZone(deneignementId, zoneId, commentaire),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['deneignement-zones', deneignementId] });
      setCommentaire('');
      setExpandedZone(null);
      onZoneUpdate?.();
      if (data.intervention_complete) {
        alert('Toutes les zones sont terminees !');
      }
    },
  });

  const handleDemarrer = (zoneId: number) => {
    if (readOnly) return;
    demarrerMutation.mutate(zoneId);
  };

  const handleTerminer = (zoneId: number) => {
    if (readOnly) return;
    terminerMutation.mutate({ zoneId, commentaire: commentaire || undefined });
  };

  const toggleExpand = (zoneId: number) => {
    setExpandedZone(expandedZone === zoneId ? null : zoneId);
    setCommentaire('');
  };

  const getStatusIcon = (statut: StatutZone) => {
    switch (statut) {
      case 'termine':
        return <CheckCircleIcon className="w-8 h-8 text-green-500" />;
      case 'en_cours':
        return <ClockIcon className="w-8 h-8 text-blue-500 animate-pulse" />;
      default:
        return <CheckCircleOutline className="w-8 h-8 text-gray-300" />;
    }
  };

  const isPending = demarrerMutation.isPending || terminerMutation.isPending;

  return (
    <div className="space-y-4">
      {/* Barre de progression */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Progression globale</span>
          <span className="text-lg font-bold text-primary-600">{progression.toFixed(0)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-blue-500 to-green-500 h-4 rounded-full transition-all duration-500"
            style={{ width: `${progression}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>{zones.filter((z) => z.statut === 'termine').length} terminees</span>
          <span>{zones.filter((z) => z.statut === 'en_cours').length} en cours</span>
          <span>{zones.filter((z) => z.statut === 'a_faire').length} a faire</span>
        </div>
      </div>

      {/* Legende priorite */}
      {zoneDemandee && zoneDemandee !== 'complete' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
          <StarIconSolid className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Zones prioritaires selon la demande
            </p>
            <p className="text-xs text-amber-600 mt-1">
              Les zones marquees d'une etoile sont demandees en priorite.
              Vous pouvez neanmoins intervenir sur toutes les zones si necessaire pour la securite.
            </p>
          </div>
        </div>
      )}

      {/* Liste des zones */}
      <div className="space-y-3">
        {zones.map((dz) => {
          const isPrioritaire = isZonePrioritaire(dz.zone.numero);
          return (
          <div
            key={dz.id}
            className={clsx(
              'bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-200',
              expandedZone === dz.id && 'ring-2 ring-primary-500',
              isPrioritaire && dz.statut === 'a_faire' && 'ring-2 ring-amber-300'
            )}
          >
            {/* En-tete de zone */}
            <div
              className={clsx(
                'p-4 flex items-center gap-4 cursor-pointer active:bg-gray-50',
                dz.statut === 'termine' && 'bg-green-50',
                isPrioritaire && dz.statut === 'a_faire' && 'bg-amber-50'
              )}
              onClick={() => toggleExpand(dz.id)}
            >
              {/* Icone de statut */}
              <div className="flex-shrink-0 relative">
                {getStatusIcon(dz.statut)}
                {isPrioritaire && dz.statut !== 'termine' && (
                  <StarIconSolid className="w-4 h-4 text-amber-500 absolute -top-1 -right-1" />
                )}
              </div>

              {/* Infos zone */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-gray-900">
                    Zone {dz.zone.numero}
                  </span>
                  {isPrioritaire && dz.statut === 'a_faire' && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                      Prioritaire
                    </span>
                  )}
                  {dz.zone.typeEnginRequis && (
                    <span
                      className={clsx(
                        'px-2 py-0.5 text-xs font-medium rounded-full text-white',
                        TYPE_ENGIN_COLORS[dz.zone.typeEnginRequis]
                      )}
                    >
                      {TYPE_ENGIN_LABELS[dz.zone.typeEnginRequis]}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600 truncate">{dz.zone.nom}</p>
                {dz.equipe && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                    <UserGroupIcon className="w-3 h-3" />
                    <span>{dz.equipe.nom}</span>
                  </div>
                )}
              </div>

              {/* Badge statut */}
              <div
                className={clsx(
                  'px-3 py-1.5 rounded-full text-sm font-medium text-white',
                  STATUT_ZONE_COLORS[dz.statut]
                )}
              >
                {STATUT_ZONE_LABELS[dz.statut]}
              </div>
            </div>

            {/* Zone etendue */}
            {expandedZone === dz.id && (
              <div className="px-4 pb-4 border-t border-gray-100">
                {/* Details */}
                <div className="py-3 space-y-2">
                  <p className="text-sm text-gray-600">{dz.zone.description}</p>
                  {dz.heureDebut && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <ClockIcon className="w-4 h-4" />
                      <span>
                        Debut: {new Date(dz.heureDebut).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  )}
                  {dz.heureFin && (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      <span>
                        Fin: {new Date(dz.heureFin).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        {dz.dureeMinutes && ` (${dz.dureeMinutes} min)`}
                      </span>
                    </div>
                  )}
                  {dz.commentaire && (
                    <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      <ChatBubbleLeftIcon className="w-4 h-4 mt-0.5 text-gray-400" />
                      <span>{dz.commentaire}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                {!readOnly && (
                  <div className="space-y-3">
                    {/* Champ commentaire pour terminer */}
                    {dz.statut === 'en_cours' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Commentaire (optionnel)
                        </label>
                        <textarea
                          value={commentaire}
                          onChange={(e) => setCommentaire(e.target.value)}
                          placeholder="Ajouter un commentaire..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          rows={2}
                        />
                      </div>
                    )}

                    {/* Boutons d'action - grands et tactiles */}
                    <div className="flex gap-3">
                      {dz.statut === 'a_faire' && (
                        <button
                          onClick={() => handleDemarrer(dz.zone.id)}
                          disabled={isPending}
                          className={clsx(
                            'flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-xl text-lg font-semibold text-white transition-all',
                            'bg-blue-500 hover:bg-blue-600 active:bg-blue-700',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            'shadow-lg shadow-blue-500/30'
                          )}
                        >
                          <PlayIcon className="w-6 h-6" />
                          <span>Demarrer</span>
                        </button>
                      )}

                      {dz.statut === 'en_cours' && (
                        <button
                          onClick={() => handleTerminer(dz.zone.id)}
                          disabled={isPending}
                          className={clsx(
                            'flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-xl text-lg font-semibold text-white transition-all',
                            'bg-green-500 hover:bg-green-600 active:bg-green-700',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            'shadow-lg shadow-green-500/30'
                          )}
                        >
                          <CheckCircleIcon className="w-6 h-6" />
                          <span>Terminer</span>
                        </button>
                      )}

                      {dz.statut === 'termine' && (
                        <div className="flex-1 flex items-center justify-center gap-3 py-4 px-6 rounded-xl bg-green-100 text-green-700">
                          <CheckCircleIcon className="w-6 h-6" />
                          <span className="text-lg font-semibold">Zone terminee</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      </div>

      {/* Message si toutes les zones sont terminees */}
      {zones.every((z) => z.statut === 'termine') && (
        <div className="bg-green-100 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircleIcon className="w-8 h-8 text-green-600" />
          <div>
            <p className="font-semibold text-green-800">Intervention terminee !</p>
            <p className="text-sm text-green-600">Toutes les zones ont ete traitees.</p>
          </div>
        </div>
      )}

      {/* Warning si aucune zone */}
      {zones.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
          <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" />
          <div>
            <p className="font-semibold text-yellow-800">Aucune zone</p>
            <p className="text-sm text-yellow-600">Les zones n'ont pas encore ete initialisees.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ZoneChecklist;
